import { useEffect, useState } from 'react';

export type HorizontalOverflow = { start: boolean; end: boolean };

// Less than this many pixels hidden is rounding, not content the user can scroll to.
const TOLERANCE = 1;

const measure = (element: HTMLElement): HorizontalOverflow => {
  const hidden = element.scrollWidth - element.clientWidth;
  if (hidden <= TOLERANCE) return { start: false, end: false };
  return { start: element.scrollLeft > TOLERANCE, end: element.scrollLeft < hidden - TOLERANCE };
};

/**
 * Whether a horizontal scroll box hides content at its start or its end, kept up to date on scroll and on resize.
 * Returns the overflow and a callback ref for the scroll box, so a box that mounts later or is replaced is measured too.
 */
export const useHorizontalOverflow = (): [HorizontalOverflow, (element: HTMLElement | null) => void] => {
  const [element, setElement] = useState<HTMLElement | null>(null);
  const [overflow, setOverflow] = useState<HorizontalOverflow>({ start: false, end: false });

  useEffect(() => {
    if (!element) return undefined;

    const update = () =>
      setOverflow((previous) => {
        const next = measure(element);
        return previous.start === next.start && previous.end === next.end ? previous : next;
      });

    update();
    element.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    // The box and the table inside it: a new column or a wider cell changes the overflow without a scroll.
    const observer = new ResizeObserver(update);
    observer.observe(element);
    for (const child of Array.from(element.children)) observer.observe(child);

    return () => {
      element.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
      observer.disconnect();
    };
  }, [element]);

  return [overflow, setElement];
};
